import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CreateWorkflowDialogComponent } from '../create-workflow-dialog/create-workflow-dialog.component';
import { WorkflowService } from '../_services/workflow.service';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from 'src/app/core/reducers';
import { selectLatestCardValues } from 'src/app/core/events';
import { UpdateCardValues } from 'src/app/core/events/_actions/inbox.actions';
import { UserService } from '../../../../core/auth/_services';
import { TaskService } from '../../../../core/timeline';
import { CategoryService, ContactsService, DrugService } from '../../../../core/lists';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LayoutUtilsService, MessageType } from '../../../../core/_base/crud';
import { WorkflowAddStandardComponent } from '../workflow-add-standard/workflow-add-standard.component';
import { WorkflowAddProcessNavResourceDialogComponent } from '../workflow-add-process-nav-resource-dialog/workflow-add-process-nav-resource-dialog.component';
import { WorkflowAddBlockerDialogComponent } from '../workflow-add-blocker-dialog/workflow-add-blocker-dialog.component';

@Component({
  selector: 'kt-workflow-list',
  templateUrl: './workflow-list.component.html',
  styleUrls: ['./workflow-list.component.scss'],
})
export class WorkflowListComponent implements OnInit, OnDestroy, OnChanges {
  readOnly = false;
  filters: any = {};
  private workflowId: any;
  statusList = new Subject<any>();
  statusesList = [];
  categoryList = [];
  workflowStatusesList: any;
  checkValues = null;
  private subscriptions: Subscription[] = [];
  showList = false;
  showCampaign = true;
  @Input() filteredView;
  @Input() workflowGroupIdFromFilteredView;
  @Input() entitiesFromFilterView;
  @Output() totalCount = new EventEmitter<any>();
  reload = false;

  ngOnChanges() {
    if (this.filteredView && this.workflowGroupIdFromFilteredView) {
      this.workflowId = this.workflowGroupIdFromFilteredView;
      this.workflowService
        .getOpenWorkflows(
          this.entitiesFromFilterView?.patients,
          this.entitiesFromFilterView?.facilities,
          this.entitiesFromFilterView?.doctors,
          this.entitiesFromFilterView?.contacts,
        )
        .subscribe((res) => {
          const totalLength = res?.data?.open_work_flow_count || 0;
          this.totalCount.emit(totalLength > 0 ? totalLength : 0);
        });
      this.prepareStatusesList();
    }
  }

  constructor(
    public dialog: MatDialog,
    private workflowService: WorkflowService,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private store: Store<AppState>,
    private userService: UserService,
    private taskService: TaskService,
    private contactsService: ContactsService,
    private drugsService: DrugService,
    private categoriesService: CategoryService,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
  ) {}

  ngOnInit(): void {
    this.getCategories(); // for categories filter
    this.subscriptions.push(
      this.activatedRoute.params.subscribe((params) => {
        // load workflow in from campaign  or accordion list
        if (params.id) {
          this.workflowId = params.id;
        }
        // load workflow inside focused view
        if (this.filteredView && this.workflowGroupIdFromFilteredView) {
          this.workflowId = this.workflowGroupIdFromFilteredView;
        }
        if (this.workflowId) {
          this.prepareStatusesList();
        }
      }),
    );

    this.subscriptions.push(
      this.store.pipe(select(selectLatestCardValues)).subscribe((res) => {
        if (res && res.workflow_queue_id !== null) {
          this.checkValues = {
            workflow_action_id: res.workflow_action_id,
            workflow_queue_id: res.workflow_queue_id,
          };
          this.store.dispatch(
            new UpdateCardValues({
              new_values: {
                group_id: null,
                workflow_queue_id: null,
                workflow_action_id: null,
              },
            }),
          );
        }
      }),
    );
  }

  openAddFlowModal() {
    const dialogRef = this.dialog.open(CreateWorkflowDialogComponent, {
      data: {
        data: null,
        isNew: true,
        workflowId: this.workflowId,
      },
      width: '600px',
      maxWidth: 'unset !important',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.reloadWorkflow) {
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  filterChanges(value: any) {
    this.filters = null;
    this.filters = value;
  }

  // Get statuses by workflow ID of the displayed campaign (workflow)
  private prepareStatusesList() {
    this.workflowService.getWorkflowStatusesList(this.workflowId).subscribe((response) => {
      const statuses = [];
      this.workflowStatusesList = [];
      response.data.map((status) => {
        this.workflowStatusesList.push(status.status);
        this.statusesList.push(status);
        statuses.push({
          label: status.status,
          value: status.number,
        });
      });
      this.statusList.next(statuses);
    });
  }

  showCampaignOnClick() {
    this.showCampaign = true;
    this.showList = false;
  }

  showListOnClick() {
    this.showList = true;
    this.showCampaign = false;
  }

  // For filters
  getCategories() {
    this.categoriesService.getCategories().subscribe((res) => {
      if (res) this.categoryList = res.data;
    });
  }

  // assign button in header
  assign(type: 'STANDARD' | 'RESOURCE' | 'BLOCKER'): void {
    this.workflowService
      .loadAllWorkflows()
      .pipe(take(1))
      .subscribe((res) => {
        const workflowGroup = res.data.find((group) => group.id === parseInt(this.workflowId, 10));
        const dialogConfig = {
          width: '800px',
          data: { workflowGroup: workflowGroup.name, workflows: workflowGroup.workflows },
        };

        const dialogRef =
          type.localeCompare('STANDARD') === 0
            ? this.dialog.open(WorkflowAddStandardComponent, dialogConfig)
            : type.localeCompare('RESOURCE') === 0
            ? this.dialog.open(WorkflowAddProcessNavResourceDialogComponent, dialogConfig)
            : this.dialog.open(WorkflowAddBlockerDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((result) => {
          if (!result) return;

          const message = this.translate.instant(
            type.localeCompare('STANDARD') === 0
              ? 'DIALOG.ASSIGN.STANDARD_MESSAGE'
              : type.localeCompare('RESOURCE') === 0
              ? 'DIALOG.ASSIGN.RESOURCE_MESSAGE'
              : 'DIALOG.ASSIGN.BLOCKER_MESSAGE',
          );

          this.layoutUtils.showActionNotification(message, MessageType.Update, 3000, false, false);
        });
      });
  }
  refreshWorkflowList() {
    this.reload = true;
    setTimeout(() => {
      this.reload = false;
      this.cdr.detectChanges();
    }, 500);
  }
}
